<template>
  <div
    v-if="isLoader"
    class="d-flex justify-content-center align-items-center"
    style="min-height: 200px"
  >
    <div
      class="spinner-border text-primary"
      role="status"
      style="width: 8rem; height: 8rem"
    >
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <div
    :style="[isLoader ? { 'pointer-events': 'none', opacity: '0.08' } : '']"
    class="card mb-25 border-0 rounded-0 bg-white letter-spacing"
  >
    <!-- ===================================================== -->
    <!-- HEADER BAR -->
    <!-- ===================================================== -->
    <div class="p-15 p-md-20 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <h5 class="mb-0 fw-bold">Liste des colis</h5>

        <!-- Toggle Filters -->
        <button
          class="btn btn-sm btn-outline-primary position-relative"
          @click="filtersOpen = !filtersOpen"
        >
          <i class="flaticon-filter me-1"></i>
          Filtres
          <span
            v-if="activeFilterCount > 0"
            class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
            style="font-size: 10px"
          >
            {{ activeFilterCount }}
          </span>
        </button>

        <!-- Reset if filters active -->
        <button
          v-if="activeFilterCount > 0"
          class="btn btn-sm btn-outline-secondary"
          @click="resetFilters"
        >
          <i class="flaticon-refresh me-1"></i> Réinitialiser
        </button>
      </div>

      <!-- Right: Search + Actions -->
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <!-- Search -->
        <div class="position-relative">
          <input
            type="text"
            class="form-control form-control-sm rounded-1 ps-4"
            style="min-width: 240px"
            placeholder="Code, téléphone, destinataire..."
            v-model="searchTerm"
            @input="handleInput"
          />
          <i
            class="flaticon-search-interface-symbol position-absolute top-50 translate-middle-y ms-2"
            style="left: 0; opacity: 0.4; pointer-events: none"
          ></i>
        </div>

        <!-- Export -->
        <button
          v-if="auth.can('EXPORT_PARCELS')"
          class="btn btn-sm btn-outline-success"
          @click="exportColis"
        >
          <i class="flaticon-file-1 me-1"></i> Export
        </button>

        <!-- Reporté aujourd'hui -->
        <button
          class="btn btn-sm btn-warning position-relative"
          data-bs-toggle="modal"
          data-bs-target="#modalExortToday"
        >
          <i class="ph ph-clock me-1"></i>
          Reporté
          <span class="badge bg-dark ms-1">{{ countExportToday }}</span>
        </button>

        <button class="btn btn-sm btn-outline-info" @click="reportTodayColis">
          <i class="ph ph-file-xls me-1"></i> .xlsx
        </button>

        <!-- Nouveau Colis -->
        <router-link
          to="/add-colis"
          class="btn btn-sm btn-success"
          v-if="auth.can('ADD_PARCEL')"
        >
          <i class="flaticon-plus me-1"></i> Nouveau Colis
        </router-link>
      </div>
    </div>

    <!-- ===================================================== -->
    <!-- ACTIVE FILTER CHIPS -->
    <!-- ===================================================== -->
    <div
      v-if="activeFilterCount > 0 && !filtersOpen"
      class="px-15 px-md-20 py-10 border-bottom d-flex flex-wrap gap-2 align-items-center"
    >
      <span class="text-muted fs-13 me-1">Filtres actifs :</span>
      <span v-if="client_filter?.name" class="badge bg-primary-subtle text-primary d-flex align-items-center gap-1">
        {{ client_filter.name }}
        <i class="ph ph-x" style="cursor:pointer" @click="removeFilter('client')"></i>
      </span>
      <span v-if="zone_filter?.name" class="badge bg-primary-subtle text-primary d-flex align-items-center gap-1">
        {{ zone_filter.name }}
        <i class="ph ph-x" style="cursor:pointer" @click="removeFilter('zone')"></i>
      </span>
      <span v-if="city_filter?.name" class="badge bg-primary-subtle text-primary d-flex align-items-center gap-1">
        {{ city_filter.name }}
        <i class="ph ph-x" style="cursor:pointer" @click="removeFilter('city')"></i>
      </span>
      <span v-if="liv_filter?.name" class="badge bg-primary-subtle text-primary d-flex align-items-center gap-1">
        {{ liv_filter.name }}
        <i class="ph ph-x" style="cursor:pointer" @click="removeFilter('liv')"></i>
      </span>
      <span v-if="etat_filter?.name" class="badge bg-primary-subtle text-primary d-flex align-items-center gap-1">
        {{ etat_filter.name }}
        <i class="ph ph-x" style="cursor:pointer" @click="removeFilter('etat')"></i>
      </span>
      <span v-if="status_filter?.name" class="badge bg-primary-subtle text-primary d-flex align-items-center gap-1">
        {{ status_filter.name }}
        <i class="ph ph-x" style="cursor:pointer" @click="removeFilter('status')"></i>
      </span>
      <span v-if="selectedDate?.[0]" class="badge bg-primary-subtle text-primary d-flex align-items-center gap-1">
        {{ selectedDate[0].format("DD MMM") }} – {{ selectedDate[1]?.format("DD MMM YYYY") }}
        <i class="ph ph-x" style="cursor:pointer" @click="removeFilter('date')"></i>
      </span>
    </div>

    <!-- ===================================================== -->
    <!-- COLLAPSIBLE FILTERS PANEL -->
    <!-- ===================================================== -->
    <div v-show="filtersOpen" class="border-bottom bg-light px-15 px-md-20 py-15">
      <div class="row g-3">
        <!-- Magasin -->
        <div class="col-sm-6 col-lg-4">
          <label class="form-label fw-medium fs-13 mb-1">Magasin</label>
          <v-select v-model="client_filter" label="name" :options="clientList" @search="filterClients" placeholder="Tous" />
        </div>
        <!-- Zone -->
        <div class="col-sm-6 col-lg-4">
          <label class="form-label fw-medium fs-13 mb-1">Zone</label>
          <v-select v-model="zone_filter" label="name" :options="zonesList" placeholder="Toutes" />
        </div>
        <!-- Ville -->
        <div class="col-sm-6 col-lg-4">
          <label class="form-label fw-medium fs-13 mb-1">Ville</label>
          <v-select v-model="city_filter" label="name" :options="citiesList" placeholder="Toutes" />
        </div>
        <!-- Livreur -->
        <div class="col-sm-6 col-lg-4">
          <label class="form-label fw-medium fs-13 mb-1">Livreur</label>
          <v-select v-model="liv_filter" label="name" :options="livreurList" @search="onSearchDrivers" placeholder="Tous" />
        </div>
        <!-- État -->
        <div class="col-sm-6 col-lg-4">
          <label class="form-label fw-medium fs-13 mb-1">État</label>
          <v-select v-model="etat_filter" label="name" :options="etatList" placeholder="Tous" />
        </div>
        <!-- Status -->
        <div class="col-sm-6 col-lg-4">
          <label class="form-label fw-medium fs-13 mb-1">Status</label>
          <v-select v-model="status_filter" label="name" :options="status" placeholder="Tous" />
        </div>
        <!-- Type date -->
        <div class="col-sm-6 col-lg-4">
          <label class="form-label fw-medium fs-13 mb-1">Par date de</label>
          <select v-model="type_date_filter" class="form-select form-select-sm">
            <option value="ADD_DATE">Date de création</option>
            <option value="LAST_UPDATE">Dernière mise à jour</option>
            <option value="PICKED_DATE">Date de ramassage</option>
            <option value="SENT_DATE">Date d'expédition</option>
            <option value="DELIVERY_DATE">Date de livraison</option>
            <option value="POSTPONED_DATE">Date reportée</option>
          </select>
        </div>
        <!-- Période -->
        <div class="col-sm-6 col-lg-4">
          <label class="form-label fw-medium fs-13 mb-1">Période</label>
          <div
            class="form-control form-control-sm d-flex align-items-center justify-content-between"
            style="cursor:pointer"
            @click="showCalendar()"
          >
            <span>
              {{ selectedDate?.[0]?.format("DD MMM YYYY") || "Date début" }}
              –
              {{ selectedDate?.[1]?.format("DD MMM YYYY") || "Date fin" }}
            </span>
            <i class="flaticon-calendar"></i>
          </div>
          <div v-show="showCalendarBox" class="position-absolute mt-1 p-2 bg-white shadow rounded" style="z-index:999">
            <MyDateRangePicker v-model="selectedDate" @onSelect="showCalendar" :enableSecondCalendar="true" />
          </div>
        </div>
        <!-- BPL -->
        <div class="col-sm-6 col-lg-4">
          <label class="form-label fw-medium fs-13 mb-1">Ajouté au BPL</label>
          <select v-model="add_bp_filter" class="form-select form-select-sm">
            <option value="">Tous</option>
            <option value="0">Non ajouté</option>
            <option value="1">Ajouté & non payé</option>
          </select>
        </div>
      </div>

      <!-- Panel actions -->
      <div class="d-flex gap-2 mt-15">
        <button class="btn btn-primary btn-sm px-4" @click="applyFilters(); filtersOpen = false">
          <i class="flaticon-filter me-1"></i> Appliquer
        </button>
        <button class="btn btn-outline-secondary btn-sm px-4" @click="resetFilters(); filtersOpen = false">
          <i class="flaticon-refresh me-1"></i> Réinitialiser
        </button>
        <button class="btn btn-link btn-sm text-muted ms-auto" @click="filtersOpen = false">
          Fermer
        </button>
      </div>
    </div>

    <!-- ===================================================== -->
    <!-- TABLE + PAGINATION -->
    <!-- ===================================================== -->
    <div class="card-body p-15 p-sm-20 p-md-25">
      <!-- ⚠️ ICI TU GARDES TON TABLEAU + PAGINATION SANS RIEN CHANGER -->
      <!-- (tbody, actions, dropdowns, pagination…) -->
      <div class="d-flex align-items-center mb-20">
        <label class="me-2 text-paragraph fw-medium">Afficher :</label>
        <select
          class="form-select form-select-sm w-auto"
          v-model="filter.per_page"
          @change="changePerPage"
        >
          <option :value="10">10</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="500">500</option>
        </select>
        <span class="ms-2 text-paragraph">lignes</span>
      </div>
      <div class="table-responsive table-scroll-mobile">
        <table class="table align-middle mb-0">
          <thead>
            <tr>
              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 ps-0"
              >
                Code d'envoi
              </th>
              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
              >
                Dernière mise à jour
              </th>
              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
              >
                Destinataire
              </th>
              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
              >
                Magasin
              </th>
              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
              >
                Etat
              </th>
              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
              >
                Status
              </th>
              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
              >
                Ville
              </th>
              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
              >
                Prix
              </th>
              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
              >
                ACTIONS
              </th>

              <th
                scope="col"
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0"
              >
                BONS
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="index">
              <th
                class="shadow-none lh-1 fw-medium text-black-emphasis title ps-0"
              >
                <div class="d-flex align-items-center">
                  <h6 v-if="row.replace === 1" class="text-warning">
                    {{ row.code }}
                    <i class="ph ph-repeat"></i>
                  </h6>
                  <h6 v-else-if="row.partial === 1" class="text-info">
                    {{ row.code }}
                    <i class="ph ph-arrows-down-up"></i>
                  </h6>
                  <h6 v-else>
                    {{ row.code }}
                  </h6>
                </div>
              </th>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ getDate(row.updated_at) }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <p class="text-nowrap">
                  <b>{{ row.receiver }}</b> <br />
                  <b>{{ oldPhone(row.phone) }}</b> <br />
                </p>
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <b v-if="row.magasin_name">Magasin : {{ row.magasin_name }} </b>

                <div v-else class="text-nowrap">
                  {{ row.customer_name }}<br />
                  {{ oldPhone(row.customer_phone) }}
                </div>
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <span
                  :class="`badge text-outline-${resolveUserStatusVariant(
                    row.situation
                  )}`"
                >
                  {{ resolveStatuParcel(row.situation) }}
                </span>
              </td>

              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <div
                  class="rounded-pill fs-13 badge text-bg-black custom-badge"
                  :style="{ '--dynamic-bg': getBadgeConfig(row).color }"
                >
                  {{ getBadgeConfig(row).text }}
                  <template v-if="getBadgeConfig(row).date">
                    <br />{{ getBadgeConfig(row).date }}
                  </template>
                  <template v-if="getBadgeConfig(row).comment">
                    <br />{{ getBadgeConfig(row).comment }}
                  </template>
                </div>
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <b>{{ row.city_name }}</b
                ><br />
                <p v-if="row.quartier && row.quartier.name">
                  {{ row.quartier.name }}
                </p>
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <b>{{ row.price }} </b>Dhs<br />
                <p style="word-wrap: break-word; width: 50%">
                  {{ row.product_nature }}
                </p>
              </td>

              <td
                class="shadow-none lh-1 fw-medium text-body-tertiary text-end pe-0"
              >
                <div class="dropdown">
                  <button
                    class="dropdown-toggle lh-1 bg-transparent border-0 shadow-none p-0 transition"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i class="flaticon-dots"></i>
                  </button>
                  <ul class="dropdown-menu">
                    <!-- Ticket -->
                    <li>
                      <button
                        class="dropdown-item d-flex align-items-center"
                        @click="TicketsPDF(row)"
                      >
                        <i
                          class="flaticon-printing lh-1 me-8 position-relative top-1"
                        ></i>
                        Ticket
                      </button>
                    </li>

                    <!-- Historique -->
                    <li>
                      <button
                        class="dropdown-item d-flex align-items-center"
                        @click="showDetailSuivi(row)"
                      >
                        <i
                          class="flaticon-clock lh-1 me-8 position-relative top-1"
                        ></i>
                        Historique
                      </button>
                    </li>

                    <!-- Infos -->
                    <li>
                      <button
                        class="dropdown-item d-flex align-items-center"
                        @click="showDetails(row)"
                      >
                        <i
                          class="flaticon-view lh-1 me-8 position-relative top-1"
                        ></i>
                        Détails
                      </button>
                    </li>

                    <!-- Edit -->
                    <!-- <li>
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="editParcel(row)"
                      >
                        <i
                          class="flaticon-pen lh-1 me-8 position-relative top-1"
                        ></i>
                        Modifier
                      </a>
                    </li> -->

                    <!-- WhatsApp Share -->
                    <li>
                      <a
                        class="dropdown-item d-flex align-items-center"
                        target="_blank"
                        :href="`https://api.whatsapp.com/send?phone=${SubstrPhone(
                          row.phone
                        )}&text=Client : ${row.customer_name} Téléphone : ${
                          row.customer_phone
                        } Code d'envoi : ${row.code} Destinataire : ${
                          row.receiver
                        } Téléphone : ${row.phone} Ville : ${
                          row.city_name
                        } Adresse : ${row.address} Commentaire : ${
                          row.note
                        } Produit : ${row.product_nature} Crbt : ${
                          row.price
                        } DH`"
                      >
                        <i
                          class="ph ph-whatsapp-logo lh-1 me-8 position-relative top-1"
                        ></i>
                        WhatsApp
                      </a>
                    </li>
                    <li>
                      <button
                        class="dropdown-item d-flex align-items-center"
                        @click="callPhone(row.phone)"
                      >
                        <i
                          class="ph ph-phone lh-1 me-8 position-relative top-1"
                        ></i>
                        Appeler destinaire
                      </button>
                    </li>

                    <!-- Share -->
                    <li>
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="ShareCust(row)"
                      >
                        <i
                          class="flaticon-send lh-1 me-8 position-relative top-1"
                        ></i>
                        Partager avec vendeur
                      </a>
                    </li>

                    <li>
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="ShareLivreur(row)"
                      >
                        <i
                          class="flaticon-send lh-1 me-8 position-relative top-1"
                        ></i>
                        Partager avec livreur
                      </a>
                    </li>

                    <!-- Conditional Delete -->
                    <li
                      v-if="
                        auth.can('PARCELS') &&
                        (user?.role_id === 1 || user?.role_id === 2) &&
                        row.status === 'NEW_PARCEL'
                      "
                    >
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="Destroy_Coli(row)"
                      >
                        <i
                          class="flaticon-delete lh-1 me-8 position-relative top-1"
                        ></i>
                        Supprimer
                      </a>
                    </li>

                    <!-- Force Delete -->
                    <li
                      v-if="
                        auth.can('PARCELS') &&
                        row.situation !== 'Facturé' &&
                        row.situation !== 'Payé' &&
                        row.situation !== 'PAID' &&
                        row.situation !== 'INVOICED' &&
                        row.invoiced === 0 &&
                        user?.role_id === 1 &&
                        auth.can('TRASH_PARCEL_ADMIN')
                      "
                    >
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="adminDestroy(row)"
                      >
                        <i
                          class="flaticon-delete lh-1 me-8 position-relative top-1"
                        ></i>
                        Forcer suppression
                      </a>
                    </li>

                    <!-- Update -->
                    <li
                      v-if="
                        auth.can('PARCELS') &&
                        (user?.role_id === 1 || user?.role_id === 2) &&
                        row.status !== 'NEW_PARCEL' &&
                        row.status !== 'WAITING_PICKUP' &&
                        row.status !== 'RETURNED' &&
                        row.status !== 'RETURNED_PARTIAL'
                      "
                    >
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="showUpdate(row)"
                      >
                        <i
                          class="flaticon-pen lh-1 me-8 position-relative top-1"
                        ></i>
                        Modifier
                      </a>
                    </li>

                    <!-- Status Actions -->
                    <li
                      v-if="
                        row.status === 'DISTRIBUTION' ||
                        (row.status === 'IN_PROGRESS' && row.invoiced === 0)
                      "
                    >
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="showStatus(row.id)"
                      >
                        <i
                          class="flaticon-edit lh-1 me-8 position-relative top-1"
                        ></i>
                        Changer Status
                      </a>
                    </li>

                    <li
                      v-if="
                        auth.can('EDIT_PRICE_PARCELS') &&
                        row.situation !== 'Facturé' &&
                        row.situation !== 'INVOICED' &&
                        row.invoiced !== 1 &&
                        row.status !== 'NEW_PARCEL' &&
                        row.status !== 'WAITING_PICKUP'
                      "
                    >
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="editPrice(row)"
                      >
                        <i
                          class="flaticon-money lh-1 me-8 position-relative top-1"
                        ></i>
                        Modifier Prix
                      </a>
                    </li>

                    <li
                      v-if="
                        auth.can('PARCELS') &&
                        auth.can('EDIT_FEES_PARCELS') &&
                        (user?.role_id === 1 || user?.role_id === 2) &&
                        row.status !== 'NEW_PARCEL' &&
                        row.status !== 'WAITING_PICKUP' &&
                        row.invoiced === 0
                      "
                    >
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="handleFeesClick(row)"
                      >
                        <i
                          class="flaticon-money lh-1 me-8 position-relative top-1"
                        ></i>
                        Modifier Frais
                      </a>
                    </li>

                    <!-- Delivered Actions -->
                    <li
                      v-if="
                        auth.can('PARCELS') &&
                        (user?.role_id === 1 || user?.role_id === 2) &&
                        (row.status === 'DELIVERED' ||
                          row.status === 'DELIVERED_PARTIAL') &&
                        row.invoiced !== 1
                      "
                    >
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="EditLivredStatut(row)"
                      >
                        <i
                          class="flaticon-edit lh-1 me-8 position-relative top-1"
                        ></i>
                        Modifier Statut (Non Livré)
                      </a>
                    </li>

                    <!-- Paid Parcel -->
                    <!-- <li
                      v-if="
                        auth.can('PARCELS') &&
                        (user?.role_id === 1 || user?.role_id === 2) &&
                        row.status !== 'DELIVERED' &&
                        row.invoiced === 0 &&
                        row.situation !== 'INVOICED' &&
                        row.paid === 0
                      "
                    >
                      <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="PaidParcel(row)"
                      >
                        <i
                          class="flaticon-money lh-1 me-8 position-relative top-1"
                        ></i>
                        Payer Colis
                      </a>
                    </li> -->
                  </ul>
                </div>
              </td>

              <td class="shadow-none fw-medium text-black-emphasis refs-td">
                <span v-show="showRefs">
                  <!-- Collapsed: show button -->
                  <button
                    v-if="row.showButtons == 0"
                    class="refs-btn refs-btn--show"
                    title="Voir Références"
                    @click="showParcelBons(row)"
                  >
                    <i class="flaticon-more"></i>
                  </button>

                  <!-- Expanded: refs list + hide button -->
                  <div v-else class="refs-expanded">
                    <div class="refs-expanded__header">
                      <span class="refs-expanded__title">Références</span>
                      <button
                        class="refs-btn refs-btn--hide"
                        title="Masquer Références"
                        @click.stop="row.showButtons = 0"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </div>
                    <ul class="refs-list">
                      <li v-if="row.delivery_note != 0">
                        <a @click="voirItem(row.delivery_note, '1')">
                          <i class="ri-file-list-line"></i> {{ row.delivery_note_ref }}
                        </a>
                      </li>
                      <li v-if="row.delivery_note_s != 0">
                        <a @click="voirItem(row.delivery_note_s, '2')">
                          <i class="ri-file-list-line"></i> {{ row.delivery_note_s_ref }}
                        </a>
                      </li>
                      <li v-if="row.reception_note_ref != 0">
                        <a :href="`/Reception/AddHC/${row.customer}/${row.reception_note_ref}`">
                          <i class="ri-file-received-line"></i> {{ row.reception_note_ref }}
                        </a>
                      </li>
                      <template v-if="row.bons && row.bons.sent && row.bons.sent.length != 0">
                        <li v-for="(sn, index) in row.bons.sent" :key="'sent-'+index">
                          <a @click="voirItem(sn.sent_id, '3')">
                            <i class="ri-send-plane-line"></i> {{ sn.sent_ref }}
                          </a>
                        </li>
                      </template>
                      <template v-if="row.bons && row.bons.arrivage && row.bons.arrivage.length != 0">
                        <li v-for="(sn, index) in row.bons.arrivage" :key="'arr-'+index">
                          <a @click="voirItem(sn.note_id, '10')">
                            <i class="ri-inbox-archive-line"></i> {{ sn.note_ref }}
                          </a>
                        </li>
                      </template>
                      <template v-if="row.bons && row.bons.dis && row.bons.dis.length != 0">
                        <li v-for="(bn, index) in row.bons.dis" :key="'dis-'+index">
                          <a @click="voirItem(bn.dis_id, '4')">
                            <i class="ri-map-pin-line"></i> {{ bn.dis_ref }}
                          </a>
                        </li>
                      </template>
                      <template v-if="row.bons && row.bons.pointage && row.bons.pointage.length != 0">
                        <li v-for="(bnp, index) in row.bons.pointage" :key="'pt-'+index">
                          <a @click="voirItem(bnp.note_id, '9')">
                            <i class="ri-check-double-line"></i> {{ bnp.note_ref }}
                          </a>
                        </li>
                      </template>
                      <template v-if="row.bons && row.bons.pay && row.bons.pay.length != 0">
                        <li v-for="(bn, index) in row.bons.pay" :key="'pay-'+index">
                          <a @click="voirItem(bn.pay_id, '6')">
                            <i class="ri-money-dollar-circle-line"></i> {{ bn.pay_ref }}
                          </a>
                        </li>
                      </template>
                      <template v-if="row.bons && row.bons.pay_z && row.bons.pay_z.length != 0">
                        <li v-for="(bz, index) in row.bons.pay_z" :key="'payz-'+index">
                          <a :href="`/edit_payments_note_zone/ref/${bz.pay_z_ref}`">
                            <i class="ri-money-dollar-circle-line"></i> {{ bz.pay_z_ref }}
                          </a>
                        </li>
                      </template>
                      <li v-if="row.rn_a_ref != 0">
                        <a @click="voirItem(row.rn_a, '7')">
                          <i class="ri-arrow-go-back-line"></i> {{ row.rn_a_ref }}
                        </a>
                      </li>
                      <li v-if="row.rn_c_ref != 0">
                        <a @click="voirItem(row.rn_c, '7')">
                          <i class="ri-arrow-go-back-line"></i> {{ row.rn_c_ref }}
                        </a>
                      </li>
                      <li v-if="row.rn_rec_ref && row.rn_rec != 0">
                        <a @click="voirItem(row.rn_rec, '7')">
                          <i class="ri-arrow-go-back-line"></i> {{ row.rn_rec_ref }}
                        </a>
                      </li>
                      <li v-if="row.rn_e_ref && row.rn_e_ref != 0">
                        <a @click="voirItem(row.rn_e, '8')">
                          <i class="ri-arrow-go-back-line"></i> {{ row.rn_e_ref }}
                        </a>
                      </li>
                      <template v-if="row.bons && row.bons.inv && row.bons.inv.length != 0">
                        <li v-for="(invoice, index) in row.bons.inv" :key="'inv-'+index">
                          <a @click="voirItem(invoice.invoice_id, '5')">
                            <i class="ri-bill-line"></i> {{ invoice.invoice_code }}
                          </a>
                        </li>
                      </template>
                    </ul>
                  </div>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="pagination-area d-flex flex-wrap mt-15 mt-md-25 mb-0 justify-content-between align-items-center gap-2"
      >
        <div class="d-flex align-items-center">
          <label class="me-2 text-paragraph fw-medium">Afficher :</label>
          <select
            class="form-select form-select-sm w-auto"
            v-model="filter.per_page"
            @change="changePerPage"
          >
            <option :value="10">10</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="500">500</option>
          </select>
          <span class="ms-2 text-paragraph">lignes</span>
        </div>
        <p class="mb-0 text-paragraph">
          Showing
          <span class="fw-bold">{{ (filter.current_page - 1) * filter.per_page + 1 }}</span>
          –
          <span class="fw-bold">{{ Math.min(filter.current_page * filter.per_page, filter.total) }}</span>
          out of <span class="fw-bold">{{ filter.total }}</span> results
        </p>
        <nav>
          <ul class="pagination mb-0 flex-wrap">
            <!-- Previous -->
            <li class="page-item" :class="{ disabled: filter.current_page === 1 }">
              <a
                class="page-link"
                href="javascript:void(0)"
                aria-label="Previous"
                @click="goToPage(filter.current_page - 1)"
              >
                <i class="flaticon-chevron-1"></i>
              </a>
            </li>

            <!-- Page numbers with ellipsis -->
            <template v-for="(page, index) in paginatedPages" :key="index">
              <li v-if="page === '...'" class="page-item disabled">
                <span class="page-link">…</span>
              </li>
              <li v-else class="page-item" :class="{ active: filter.current_page === page }">
                <a
                  href="javascript:void(0)"
                  class="page-link"
                  @click="goToPage(page as number)"
                >
                  {{ page }}
                </a>
              </li>
            </template>

            <!-- Next -->
            <li class="page-item" :class="{ disabled: filter.current_page === filter.last_page }">
              <a
                class="page-link"
                href="javascript:void(0)"
                aria-label="Next"
                @click="goToPage(filter.current_page + 1)"
              >
                <i class="flaticon-chevron"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>

  <!-- Modal Filter -->
  <div class="modal fade" id="filterLeadModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header">
          <h1 class="modal-title fs-5">Filtrer</h1>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body">
          <form class="row">
            <!-- Magasin -->
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label>Magasin:</label>
                <v-select
                  class="custom-select-height"
                  v-model="client_filter"
                  label="name"
                  :options="clientList"
                  @search="filterClients"
                />
              </div>
            </div>

            <!-- Zone -->
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label>Zone:</label>
                <v-select
                  class="custom-select-height"
                  v-model="zone_filter"
                  label="name"
                  :options="zonesList"
                />
              </div>
            </div>

            <!-- Ville -->
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label>Ville:</label>
                <v-select
                  class="custom-select-height"
                  v-model="city_filter"
                  label="name"
                  :options="citiesList"
                />
              </div>
            </div>

            <!-- Livreur -->
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label>Livreur:</label>
                <v-select
                  class="custom-select-height"
                  v-model="liv_filter"
                  style="border-color: #0000002b"
                  label="name"
                  :options="livreurList"
                  @search="onSearchDrivers"
                >
                  <!--  -->
                  <template #no-options>
                    Taper un mot pour chercher un livreur
                  </template>
                </v-select>
              </div>
            </div>

            <!-- Etat -->
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label>Etat:</label>
                <v-select
                  class="custom-select-height"
                  v-model="etat_filter"
                  style="border-color: #0000002b"
                  label="name"
                  :options="etatList"
                />
              </div>
            </div>

            <!-- Status -->
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label>Status:</label>
                <v-select
                  class="custom-select-height"
                  v-model="status_filter"
                  style="border-color: #0000002b"
                  label="name"
                  :options="status"
                />
              </div>
            </div>

            <!-- Par Date de -->
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label>Par Date de:</label>
                <select v-model="type_date_filter" class="form-control">
                  <option value="ADD_DATE">Date de création</option>
                  <option value="LAST_UPDATE">Dernière Mise à jour</option>
                  <option value="PICKED_DATE">Date de ramassage</option>
                  <option value="SENT_DATE">Date d'expédition</option>
                  <option value="DELIVERY_DATE">Date de livraison</option>
                  <option value="POSTPONED_DATE">Date reportée</option>
                </select>
              </div>
            </div>

            <!-- Ajouté Au BPL -->
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label>Ajouté Au BPL:</label>
                <select v-model="add_bp_filter" class="form-control">
                  <option value="">Tous</option>
                  <option value="0">Non Ajouté Au BPL</option>
                  <option value="1">Ajouté Au BPL et Non Payé</option>
                </select>
              </div>
            </div>
            <!-- <div class="col-12">
              <div class="form-group mb-3">
                <label>Par date:</label>
                
              </div>
            </div> -->

            <!-- Afficher Réf -->
            <!-- <div class="col-md-4">
              <div class="form-group mb-3">
                <label>Afficher Réf:</label>
                <select
                  v-model="ref_filter"
                  class="form-control"
                  @change="showRefsEdit"
                >
                  <option value="">Tous</option>
                  <option value="1">Afficher les Ref</option>
                  <option value="0">Ne pas afficher les Refs</option>
                </select>
              </div>
            </div> -->

            <!-- Prix 0DH (with permission) -->
            <!-- <template v-if="auth.can('FILTER_PARCEL_PRICE')">
              <div class="col-md-4">
                <div class="form-group mb-3">
                  <label>Prix 0DH:</label>
                  <select v-model="price_filter" class="form-control">
                    <option value="">Tous</option>
                    <option value="1">Oui</option>
                    <option value="0">Non</option>
                  </select>
                </div>
              </div>
            </template> -->
          </form>
        </div>

        <div class="modal-footer">
          <!-- <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Fermer
          </button> -->
          <button type="button" @click="removeFilter" class="btn btn-primary">
            Annuler
          </button>
          <button type="button" @click="filterList" class="btn btn-warning">
            Appliquer
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- exported todaycolis modal -->
  <div class="modal fade" id="modalExortToday" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5">Colis reportés pour aujourd'hui</h1>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body">
          <exported-today />
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Status Update Modal using Bootstrap -->
  <div
    class="modal fade"
    id="modal_update_status"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header">
          <h5 class="modal-title">Changer le statut du colis</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Fermer"
          ></button>
        </div>
        <div class="modal-body">
          <form>
            <!-- Statut -->
            <div class="mb-3">
              <label for="statut" class="form-label">Statut</label>
              <v-select
                class="custom-select-height"
                id="statut"
                v-model="statutColi"
                label="name"
                placeholder="Choisir un statut"
                :options="StatusList2"
              />
            </div>
            <!-- Commentaire -->
            <div class="mb-3">
              <label for="note" class="form-label">Commentaire</label>
              <textarea
                v-model="commentStatu"
                name="note"
                rows="3"
                class="form-control"
                placeholder="Écrire un commentaire ici..."
              ></textarea>
            </div>
            <!-- Date le (if POSTPONED or PROGRAMED) -->
            <div
              class="mb-3"
              v-if="
                statutColi.code === 'POSTPONED' ||
                statutColi.code === 'PROGRAMED'
              "
            >
              <label for="datepicker-dateformat2" class="form-label"
                >Date le :</label
              >
              <input
                type="date"
                id="datepicker-dateformat2"
                v-model="date_retour"
                class="form-control"
                :min="minDate"
              />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button
            v-if="auth.can('PARCELS')"
            type="button"
            class="btn btn-primary"
            @click="editPriceStatut"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Status Update Modal using Bootstrap -->
  <div
    class="modal fade"
    id="modal_detail_suivi"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header">
          <h5 class="modal-title">Historique Colis</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Fermer"
          ></button>
        </div>
        <div class="modal-body">
          <HistoryColis
            v-if="colisId"
            :colisId="colisId"
            :isOpen="isOpen"
            :status="status"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Status details colios -->
  <div
    class="modal fade"
    id="modal_detail_colis"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header">
          <h5 class="modal-title">Détails Colis</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Fermer"
          ></button>
        </div>
        <div class="modal-body">
          <DetailsColis
            v-if="coliDetails"
            :coliDetails="coliDetails"
            :isOpen="isOpen"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Update Parcel Modal using Bootstrap -->
  <div class="modal fade" id="modal_update" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header">
          <h5 class="modal-title">Modifier Colis</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Fermer"
          ></button>
        </div>
        <div class="modal-body">
          <UpdateParcel
            v-if="rowUpdate"
            :rowColis="rowUpdate"
            :getRecords="getRecords"
            :isOpenEdit="isOpenEdit"
            :hideModal="hideModal"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Update Fees Modal using Bootstrap -->
  <div class="modal fade" id="modal_fees_edit" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header">
          <h5 class="modal-title">Modifier les frais</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Fermer"
          ></button>
        </div>
        <div class="modal-body">
          <UpdateFeesParcel
            v-if="rowUpdateFees"
            :rowColis="rowUpdateFees"
            :getRecords="getRecords"
            :isOpenFees="isOpenFees"
            :hideModal="hideModal"
          />
        </div>
      </div>
    </div>
  </div>

  <!--  update price parcel -->
  <!-- Update Price Parcel Modal using Bootstrap -->
  <div
    class="modal fade"
    id="modal_update_price"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header">
          <h5 class="modal-title">Modification du prix de colis</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Fermer"
          ></button>
        </div>
        <div class="modal-body">
          <form>
            <div class="mb-3">
              <label for="price" class="form-label">Prix</label>
              <input
                type="number"
                id="price"
                v-model="PriceColis.price"
                class="form-control"
              />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Annuler
          </button>
          <button type="button" class="btn btn-primary" @click="EditPriceColis">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script  lang="ts">
import { nextTick, onMounted, reactive, ref, computed } from "vue";
import flatpickr from "flatpickr";
import { useAuthStore } from "../../stores/auth";
import { useAppToast } from "@/composables/useAppToast";
import api from "@/utils/axios";
import moment from "moment";
import ExportedToday from "@/components/Colis/ExportedToday.vue";
import HistoryColis from "@/components/Colis/HistoryColis.vue";
import UpdateParcel from "@/components/Colis/UpdateParcel.vue";
import DetailsColis from "@/components/Colis/DetailsColis.vue";
import UpdateFeesParcel from "@/components/Colis/UpdateFeesParcel.vue";
import MyDateRangePicker from "@/components/MyDateRangePicker.vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
moment.locale("fr");
export default {
  name: "ColisList",
  components: {
    ExportedToday,
    HistoryColis,
    DetailsColis,
    UpdateFeesParcel,
    UpdateParcel,
    MyDateRangePicker,
  },
  setup: () => {
    const { showToast } = useAppToast();
    const leadPlaceholder = "Select Lead Date";
    const auth = useAuthStore();
    const user = auth.user;

    const rows = ref([]);
    const direction = ref(true); // if you use RTL boolean; adjust to your prop type
    const isLoader = ref(false);
    const ExportToday = ref(null);
    const countExportToday = ref(0);

    const minDate = ref(
      moment(
        new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate() + 1
        )
      ).format("YYYY-MM-DD")
    );

    const columns = [
      { label: "Code d'envoi", field: "code" },
      { label: "Dernière mise à jour", field: "updated_at" },
      { label: "Destinataire", field: "receiver" },
      { label: "Magasin", field: "magasin_m" },
      { label: "Etat", field: "situation" },
      { label: "Status", field: "status" },
      { label: "Ville", field: "city_name" },
      { label: "Prix", field: "price" },
      { label: "Action", field: "action" },
      { label: "", field: "bn" },
    ];

    const filter = reactive({
      surrent_page: 1,
      per_page: 10,

      searchTerm: "",
      searchCustomer: "",
      searchZone: "",
      searchLiv: "",
      searchCity: "",
      searchEtat: "",
      searchStatut: "",
      searchSDate: "",
      searchEDate: "",
      searchTypeDate: "",
      searchBPL: "",
      searchRef: "",
      searchEmail: "",
      searchPrice: "",

      // (optional) pagination/meta from API:
      total: 0,
      last_page: 1,
      current_page: 1,
    });

    const zone_filter = ref({ id: "", name: "" });
    const client_filter = ref({ id: "", name: "" });
    const liv_filter = ref({ id: "", name: "" });
    const city_filter = ref({ id: "", name: "" });
    const status_filter = ref({ id: "", name: "", code: "" });
    const date_filter = ref([]);
    const etat_filter = ref<any>("");
    const type_date_filter = ref("");
    const add_bp_filter = ref("");
    const ref_filter = ref("");
    const email_filter = ref("");
    const price_filter = ref("");
    const searchTerm = ref("");

    const coliDetails = ref<any>({});
    const statutColi = ref<any>({});
    const commentStatu = ref("");
    const date_retour = ref("");
    const statuColisID = ref(0);

    const formStatus = reactive({
      id: 0,
      status: "",
      status_id: 0,
      comment: "",
    });

    interface Status {
      code: string;
      name: string;
      code_color?: string;
    }

    const status = ref<Status[]>([]);
    const StatusList2 = ref<Status[]>([]);

    const clientList = ref<Status[]>([]);
    const zonesList = ref<Status[]>([]);
    const citiesList = ref<Status[]>([]);
    const livreurList = ref<Status[]>([]);
    const etatList = ref(["Tous", "Non Payé", "Payé", "Facturé"]);

    function convert(date) {
      // example: to YYYY-MM-DD (adapt to your backend)
      const d = new Date(date);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${d.getFullYear()}-${m}-${day}`;
    }

    function getDate(value) {
      if (value) return moment(String(value)).format("YYYY-MM-DD HH:mm");
      else return "";
    }
    const changePerPage = () => {
      filter.current_page = 1; // reset to first page when changing limit
      getRecords();
    };

    function buildQuery() {
      const params = new URLSearchParams({
        searchTerm: filter.searchTerm || "",
        searchCustomer: filter.searchCustomer || "",
        searchZone: filter.searchZone || "",
        searchLiv: filter.searchLiv || "",
        searchCity: filter.searchCity || "",
        searchEtat: filter.searchEtat || "",
        searchStatut: filter.searchStatut || "",
        searchSDate: filter.searchSDate || "",
        searchEDate: filter.searchEDate || "",
        searchTypeDate: filter.searchTypeDate || "",
        searchBPL: filter.searchBPL || "",
        searchRef: filter.searchRef || "",
        searchEmail: filter.searchEmail || "",
        searchPrice: filter.searchPrice || "",
        page: String(filter.current_page || 1),
        per_page: String(filter.per_page || 10),
      });
      return `/api/auth/colis_list?${params.toString()}`;
    }

    onMounted(async () => {
      flatpickr("#lead-calendar", {
        altInput: true,
        altFormat: "F j, Y",
        dateFormat: "Y-m-d",
        defaultDate: "today",
      });

      flatpickr("#basic-calendar", {
        dateFormat: "Y-m-d H:i",
      });
      getRecords();
      fetchStatus();

      try {
        const response = await api.get(`/api/auth/ListZone`);
        if (response && response.data) {
          zonesList.value = response.data;
          zonesList.value.unshift({ code: "0", name: "Tous" });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }

      try {
        const { data } = await api.get(`/api/auth/citiesList`);

        if (Array.isArray(data)) {
          citiesList.value = data;

          // Évite les doublons
          if (
            !citiesList.value.some(
              (city) => city.code === "0" && city.name === "Tous"
            )
          ) {
            citiesList.value.unshift({ code: "0", name: "Tous" });
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des villes :", error);
        // showToast("Impossible de charger la liste des villes", "error")
      }

      try {
        const response = await api.get(`/api/auth/fetchStatus`);
        if (response && response.data) {
          StatusList2.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    });

    async function getRecords() {
      isLoader.value = true;
      try {
        const url = buildQuery();
        const { data } = await api.get(url);

        rows.value = data.users.data;

        // Keep your search fields; only update pagination/meta you need:
        filter.current_page = data.users.current_page;
        filter.per_page = data.users.per_page;
        filter.total = data.users.total;
        filter.last_page = data.users.last_page;

        // If you actually need all fields returned from backend:
        // Object.assign(users, { ...users, ...data.users })

        countExportToday.value = data.exported;
      } finally {
        isLoader.value = false;
      }
    }

    function filterList() {
      console.log(selectedDate);
      filter.searchZone = zone_filter.value?.id || "";
      filter.searchCustomer = client_filter.value?.id || "";
      filter.searchLiv = liv_filter.value?.id || "";
      filter.searchCity = city_filter.value?.id || "";
      filter.searchEtat = etat_filter.value || "";
      filter.searchStatut = status_filter.value?.code || "";

      //   filter.searchSDate = myInput.value?.dateRange?.start
      //     ? convert(myInput.value.dateRange.start)
      //     : "";
      //   filter.searchEDate = myInput.value?.dateRange?.end
      //     ? convert(myInput.value.dateRange.end)
      //     : "";

      filter.searchTypeDate = type_date_filter.value || "";
      filter.searchBPL = add_bp_filter.value || "";
      filter.searchRef = ref_filter.value || "";
      filter.searchEmail = email_filter.value || "";
      filter.searchTerm = searchTerm.value || "";
      filter.searchPrice = price_filter.value || "";

      filter.current_page = 1;
      getRecords();
      hideModal("filterLeadModal");
    }

    async function exportColis() {
      filter.searchZone = zone_filter.value?.id || "";
      filter.searchCustomer = client_filter.value?.id || "";
      filter.searchLiv = liv_filter.value?.id || "";
      filter.searchCity = city_filter.value?.id || "";
      filter.searchEtat = etat_filter.value || "";
      filter.searchStatut = status_filter.value?.code || "";

      //   filter.searchSDate = myInput.value?.dateRange?.start
      //     ? convert(myInput.value.dateRange.start)
      //     : "";
      //   filter.searchEDate = myInput.value?.dateRange?.end
      //     ? convert(myInput.value.dateRange.end)
      //     : "";

      filter.searchTypeDate = type_date_filter.value || "";
      filter.searchBPL = add_bp_filter.value || "";
      filter.searchRef = ref_filter.value || "";
      filter.searchEmail = email_filter.value || "";
      filter.searchTerm = searchTerm.value || "";
      filter.searchPrice = price_filter.value || "";

      try {
        const res = await api.get(
          `/api/auth/ExportColis?searchTerm=${filter.searchTerm}&searchCustomer=${filter.searchCustomer}&searchZone=${filter.searchZone}&searchLiv=${filter.searchLiv}&searchCity=${filter.searchCity}&searchEtat=${filter.searchEtat}&searchStatut=${filter.searchStatut}&searchSDate=${filter.searchSDate}&searchEDate=${filter.searchEDate}&searchTypeDate=${filter.searchTypeDate}&searchBPL=${filter.searchBPL}`,
          { responseType: "arraybuffer" }
        );

        const fileURL = window.URL.createObjectURL(
          new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );

        const fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.download = "ExportColis.xlsx";
        fileLink.click();
      } catch (error) {
        if (error.response) {
          // Erreur serveur (ex: 500)
          console.error(
            "Erreur serveur :",
            error.response.status,
            error.response.data
          );
          // Exemple toast ou showToaste
          // showToast("Une erreur serveur est survenue lors de l'export (500)", "error");
          showToast(
            "Une erreur serveur est survenue lors de l'export.",
            "error"
          );
        } else if (error.request) {
          // Pas de réponse du serveur
          console.error("Aucune réponse du serveur :", error.request);
          showToast(
            "Impossible de contacter le serveur. Vérifiez votre connexion.",
            "error"
          );
        } else {
          // Erreur autre (ex: code JS)
          console.error("Erreur lors de la requête :", error.message);
          showToast("Une erreur inattendue est survenue.", "error");
        }
      }
    }

    // function removeFilter() {
    //   // reset users filters
    //   filter.searchZone = "";
    //   filter.searchCustomer = "";
    //   filter.searchPrice = "";
    //   filter.searchLiv = "";
    //   filter.searchCity = "";
    //   filter.searchEtat = "";
    //   filter.searchStatut = "";

    //   filter.searchSDate = "";
    //   filter.searchEDate = "";
    //   filter.searchTypeDate = "";
    //   filter.searchBPL = "";
    //   filter.searchRef = "";
    //   filter.searchEmail = "";
    //   filter.searchTerm = "";
    //   filter.current_page = 1;

    //   // reset local UI models
    //   etat_filter.value = "";
    //   add_bp_filter.value = "";
    //   ref_filter.value = "";
    //   email_filter.value = "";
    //   type_date_filter.value = "";
    //   price_filter.value = "";
    //   searchTerm.value = "";

    //   zone_filter.value = { id: "", name: "" };
    //   client_filter.value = { id: "", name: "" };
    //   liv_filter.value = { id: "", name: "" };
    //   city_filter.value = { id: "", name: "" };
    //   status_filter.value = { id: "", name: "", code: "" };

    //   // clear date range picker model
    //   //   if (!myInput.value) myInput.value = { dateRange: {} }
    //   //   myInput.value.dateRange = {}

    //   getRecords();
    //   hideModal("filterLeadModal");
    // }

    async function fetchStatus() {
      try {
        const response = await api.get("/api/auth/AllStatus");
        if (response && response.data) {
          status.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching statuses:", error);
      }
    }

    function goToPage(page) {
      if (page < 1 || page > filter.last_page) return;
      filter.current_page = page;
      getRecords();
    }

    function oldPhone(val) {
      if (val && val !== "" && val != null) {
        let phone = "";
        if (val.substring(0, 4) === "+212" && val.length === 13) {
          return `0${val.slice(4)}`;
        }
        if (val.substring(0, 3) === "212" && val.length === 12) {
          phone = `0${val.slice(2)}`;
          return phone;
        }
        if (val.substring(0, 1) === "0" && val.length === 10) {
          return val;
        }
        if (
          val.substring(0, 1) === "6" ||
          (val.substring(0, 1) === "7" && val.length === 9)
        ) {
          // phone = "+212" + val;
          return val;
        }
        if (val.substring(0, 5) === "00212" && val.length === 14) {
          return `0${val.substring(0, 5)}`;
        }
      } else {
        return val;
      }
    }
    function globalHelper(s: string) {
      if (!s || s === "undefined") {
        return null; // always return null for invalid values
      }

      return status.value.find((o) => o.code === s) || null;
    }

    function removeHours(value) {
      if (value && value !== "") {
        const datePart = value.split(" ")[0];
        return datePart;
      }
    }
    function reported(date) {
      const currentDate = new Date();
      const reportedDate = new Date(date);
      if (reportedDate.getTime() <= currentDate.getTime()) {
        return true;
      } else {
        return false;
      }
    }
    function resolveStatuParcel(status) {
      if (status === "POSTPONED") return "Reporté";
      if (status === "DELIVERED") return "Livré";
      if (status === "PAID") return "Payé";
      if (status === "NOT_PAID") return "Non Payé";
      if (status === "Payé") return "Payé";
      if (status === "CANCELED") return "Annulé";
      if (status === "UNREACHABLE") return "Injoignable";
      if (status === "POSTPONED") return "Reporté";
      if (status === "RETURNED") return "Retourné";
      if (status === "RECEIVED") return "Reçu";
      if (status === "OUT_OF_AREA") return "Hors-zone";
      if (status === "NOANSWER") return "Pas de réponse + SMS";
      if (status === "SENTED") return "Expédié";
      if (status === "REFUSE") return "Refusé";
      if (status === "ONV") return "En Voyage";
      if (status === "INT") return "Client intéréssé";
      if (status === "POSTPONED_TEAM") return "Reporté ( SUIVI )";
      if (status === "CANCELED_TEAM") return "Annulé ( SUIVI )";
      if (status === "VOICEMAIL_TEAM") return "Boite Vocal ( SUIVI )";
      if (status === "EN") return "Erreur Numéro";
      return status;
    }

    function resolveUserStatusVariant(status) {
      if (status === "PICKED_UP") return "warning";
      if (status === "DELIVERED") return "success";
      if (status === "PAID") return "info";
      if (status === "NOT_PAID") return "danger";
      if (status === "Payé") return "info";
      if (status === "Non Payé") return "warning";
      if (status === "Facturé") return "info";
      return "primary";
    }

    function getStatusName(code?: string) {
      return code ? globalHelper(code)?.name || "" : "";
    }

    function getStatusColor(code?: string) {
      return code ? globalHelper(code)?.code_color || "#000" : "#000";
    }

    function formatReported(row: any) {
      return removeHours(row.reported_date);
    }

    // Decide what to show in badge
    function getBadgeConfig(row: any) {
      // IN_PROGRESS with secondary status
      if (
        row.status === "IN_PROGRESS" &&
        row.status_second &&
        getStatusName(row.status_second)
      ) {
        if (
          ["POSTPONED", "PROGRAMED", "POSTPONED_TODAY"].includes(
            row.status_second
          )
        ) {
          return {
            text: getStatusName(row.status_second),
            date: formatReported(row),
            color: reported(row.reported_date)
              ? "#E50A0A"
              : getStatusColor(row.status_second),
          };
        }
        return {
          text: getStatusName(row.status_second),
          color: getStatusColor(row.status_second),
        };
      }

      // RETURNED but not REFUSE
      if (
        row.status === "RETURNED" &&
        (!row.status_second || row.status_second !== "REFUSE") &&
        row.last_statut_comment
      ) {
        return {
          text:
            row.rn_e && row.rn_e !== 0
              ? `${row.statut?.s_name} Endommagé`
              : row.statut?.s_name,
          comment: row.last_statut_comment,
          color: row.statut?.code_color,
        };
      }

      // RETURNED with REFUSE
      if (
        row.status === "RETURNED" &&
        row.status_second === "REFUSE" &&
        getStatusName(row.status_second)
      ) {
        return {
          text:
            row.rn_e && row.rn_e !== 0
              ? `Retourné Endommagé - ${getStatusName(row.status_second)}`
              : `Retourné - ${getStatusName(row.status_second)}`,
          comment: row.last_statut_comment,
          color: getStatusColor(row.status_second),
        };
      }

      // Default
      return {
        text:
          row.rn_e && row.rn_e !== 0 && row.status === "RETURNED"
            ? `${row.statut.s_name} Endommagé`
            : row.statut.s_name,
        color: row.statut.code_color,
      };
    }

    let debounceTimeout: number | undefined;

    // function onSearchClients(search, loading) {
    //   clearTimeout(searchTimeout)
    //   searchTimeout = setTimeout(() => {
    //     filterClients(search, loading)
    //   }, 300) // Debounce time in milliseconds
    // },

    async function filterClients(search: string, loading: { value: boolean }) {
      const query = search.trim().toLowerCase();

      if (!query) {
        clientList.value = [];
        return;
      }

      // Debounce to avoid too many API calls
      if (debounceTimeout) clearTimeout(debounceTimeout);

      debounceTimeout = window.setTimeout(async () => {
        try {
          const res = await api.get(`/api/auth/ListCustsFilter`, {
            params: { query },
          });
          clientList.value = res.data;
          clientList.value.unshift({ code: "0", name: "Tous" });
        } catch (error) {
          console.error("Error fetching clients:", error);
          clientList.value = [];
        }
      }, 300); // 300ms debounce
    }

    async function onSearchDrivers(
      search: string,
      loading: { value: boolean }
    ) {
      const query = search.trim().toLowerCase();

      if (!query) {
        livreurList.value = [];
        return;
      }

      // Debounce to avoid too many API calls
      if (debounceTimeout) clearTimeout(debounceTimeout);

      debounceTimeout = window.setTimeout(async () => {
        try {
          const res = await api.get(`/api/auth/LivsList`, {
            params: { query },
          });
          livreurList.value = res.data;
          livreurList.value.unshift({ code: "0", name: "Tous" });
        } catch (error) {
          console.error("Error fetching clients:", error);
          livreurList.value = [];
        }
      }, 300); // 300ms debounce
    }

    async function reportTodayColis() {
      try {
        const res = await api.get(`/api/auth/TodayExportColis`, {
          responseType: "arraybuffer",
        });

        const fileURL = window.URL.createObjectURL(
          new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );

        const fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.download = "ExportReportedColis.xlsx";
        fileLink.click();
      } catch (error) {
        if (error.response) {
          // Erreur serveur (ex: 500, 404...)
          console.error(
            "Erreur serveur :",
            error.response.status,
            error.response.data
          );
          showToast(
            `Erreur serveur (${error.response.status}) lors de l'export des colis.`,
            "error"
          );
        } else if (error.request) {
          // Aucune réponse du serveur
          console.error("Aucune réponse du serveur :", error.request);
          showToast(
            "Impossible de contacter le serveur. Vérifiez votre connexion.",
            "error"
          );
        } else {
          // Erreur autre (JS, Axios config, etc.)
          console.error("Erreur lors de la requête :", error.message);
          showToast(
            "Une erreur inattendue est survenue lors de l'export.",
            "error"
          );
        }
      }
    }

    const showRefs = ref(true);
    function showRefsEdit() {
      if (ref_filter.value == "1") {
        showRefs.value = true;
      } else {
        showRefs.value = false;
      }
    }

    // Mapping type to endpoint
    const pdfEndpoints = {
      1: "DELPDF",
      2: "DeliveryPDF",
      3: "SentPDF",
      4: "DISTPDF",
      5: "InvoicePdf",
      6: "PAYMPDF",
      7: "ReturnPDF",
      8: "ReturnPDF",
      9: "PonitPDF",
      10: "ArrivagePDF",
    };

    async function showParcelBons(row) {
      try {
        const res = await api.get(`/api/auth/BonsParcel/${row.id}`);
        if (res.data.msg === "success") {
          row.bons = res.data.data;
          row.showButtons = 1;
        }
      } catch (error) {
        console.error(error);
        showToast("Erreur lors de la récupération des bons", "error");
      }
    }

    async function voirItem(item, type) {
      const endpoint = pdfEndpoints[type];
      if (!endpoint) {
        console.warn(`PDF type "${type}" non géré`);
        return;
      }

      try {
        const res = await api.get(`/api/auth/${endpoint}/${item}`, {
          responseType: "arraybuffer",
        });

        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        window.open(url, "_blank");
        // Optional: download automatically
        // const link = document.createElement('a');
        // link.href = url;
        // link.download = `${item}.pdf`;
        // link.click();
      } catch (error) {
        console.error(error);
        showToast("Erreur lors du téléchargement du PDF", "error");
      }
    }

    // -------------------------------
    // Function to generate PDF ticket
    async function TicketsPDF(row) {
      try {
        const response = await api.get(`/api/auth/TicketParcelPDF/${row.id}`, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        // Optionally, revoke the object URL after some time
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      } catch (error) {
        console.error("Erreur lors du téléchargement du ticket PDF:", error);
        showToast("Erreur lors du téléchargement du ticket PDF", "error");
      }
    }

    // -------------------------------
    // Function to show parcel history
    const colisId = ref(null);
    const isOpen = ref(false);

    async function showDetailSuivi(row) {
      isOpen.value = false;
      colisId.value = null;

      await nextTick();

      colisId.value = row.id;
      isOpen.value = true;
      await nextTick();
      const modalEl = document.getElementById("modal_detail_suivi");
      if (modalEl) {
        const modal = new Modal(modalEl);
        isOpen.value = true;
        modal.show();
      }
      // TODO: Open history modal
    }

    // -------------------------------
    // Function to show parcel info
    async function showDetails(row) {
      isOpen.value = false;
      coliDetails.value = null;

      await nextTick();
      coliDetails.value = row;
      isOpen.value = true;

      await nextTick();

      const modalEl = document.getElementById("modal_detail_colis");
      if (modalEl) {
        const modal = new Modal(modalEl);
        isOpen.value = true;
        modal.show();
      }
      // TODO: Open info modal
    }

    // -------------------------------
    // Function to edit parcel
    async function editParcel(row) {
      console.log("Editing parcel:", row);
      // TODO: Open edit modal
    }

    // -------------------------------
    // Function to format phone for WhatsApp
    async function SubstrPhone(phone) {
      // Example: remove first zero and add country code
      return phone.startsWith("0") ? "212" + phone.slice(1) : phone;
    }

    // -------------------------------
    // Share parcel with seller
    async function ShareCust(row) {
      try {
        const res = await api.get(`/api/auth/SearchPhone`, {
          params: { type: 1, code: row.code },
        });
        if (res.data.msg === "success") {
          const phone = await SubstrPhone(res.data.user.phone);
          const message = [
            `Client : ${res.data.user.store}`,
            `Téléphone : ${row.customer_phone}`,
            `Code d'envoi : ${row.code}`,
            `Destinataire : ${row.receiver}`,
            `Téléphone : ${row.phone}`,
            `Ville : ${row.city_name}`,
            `Adresse : ${row.address}`,
            `Commentaire : ${row.note}`,
            `Produit : ${row.product_nature}`,
            `Crbt : ${row.price} DH`,
          ].join("\n");
          window.open(
            `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(
              message
            )}`,
            "_blank"
          );
        }
      } catch (error) {
        console.error("Erreur lors du partage avec vendeur :", error);
      }
    }

    async function ShareLivreur(row) {
      try {
        const res = await api.get(`/api/auth/SearchPhone`, {
          params: { type: 2, code: row.code },
        });
        if (res.data.msg === "success") {
          const phone = await SubstrPhone(res.data.user.phone);
          const message = [
            `Client : ${row.customer_name}`,
            `Téléphone : ${row.customer_phone}`,
            `Code d'envoi : ${row.code}`,
            `Destinataire : ${row.receiver}`,
            `Téléphone : ${row.phone}`,
            `Ville : ${row.city_name}`,
            `Adresse : ${row.address}`,
            `Commentaire : ${row.note}`,
            `Produit : ${row.product_nature}`,
            `Crbt : ${row.price} DH`,
          ].join("\n");
          window.open(
            `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(
              message
            )}`,
            "_blank"
          );
        }
      } catch (error) {
        console.error("Erreur lors du partage avec livreur :", error);
      }
    }

    // -------------------------------
    // Delete a parcel
    async function Destroy_Coli(row: any) {
      console.log("Deleting parcel:", row);
      Swal.fire({
        text: "Vous voulez supprimer ce colis ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Oui, supprimer!",
      }).then(async (result: any) => {
        if (result.isConfirmed) {
          const data = new FormData();
          data.append("parcel_id", row.id);
          data.append("parcel_code", row.code);
          try {
            const res = await api.post(`/api/auth/removeParcel`, data);
            if (res.data.message === "success") {
              showToast("Colis supprimé avec succès", "success");
              getRecords();
            } else if (res.data.message === "error") {
              showToast("Vous pouvez pas supprimer ce colis", "error");
            }
          } catch (error) {
            showToast("Erreur lors de la suppression du colis", "error");
          }
        }
      });
    }

    // -------------------------------
    // Force delete a parcel (admin)
    async function adminDestroy(row) {
      console.log("Force deleting parcel:", row);
      Swal.fire({
        text: "Vous voulez supprimer ce colis ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Oui, supprimer!",
      }).then(async (result: any) => {
        if (result.isConfirmed) {
          const data = new FormData();
          data.append("parcel_id", row.id);
          data.append("parcel_code", row.code);
          try {
            const res = await api.post(`/api/auth/adminRemoveParcel`, data);
            if (res.data.message === "success") {
              showToast("Colis supprimé avec succès", "success");
              getRecords();
            } else if (res.data.message === "error") {
              showToast("Vous pouvez pas supprimer ce colis", "error");
            }
          } catch (error) {
            showToast("Erreur lors de la suppression du colis", "error");
          }
        }
      });
    }

    // -------------------------------
    const rowUpdate = ref(null);
    const isOpenEdit = ref(false);
    // Show update modal
    async function showUpdate(row) {
      rowUpdate.value = null;
      isOpenEdit.value = false;
      rowUpdate.value = row;
      nextTick(() => {
        const modalEl = document.getElementById("modal_update");
        console.log(modalEl);
        if (modalEl) {
          isOpenEdit.value = true; // triggers watcher in child

          const modal = new Modal(modalEl);
          modal.show();
        }
      });
    }

    function hideModal(idModal) {
      const modalEl = document.getElementById(idModal);
      const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
      if (modal) {
        modal.hide();
      }
    }

    // -------------------------------
    // Show status modal
    async function showStatus(id) {
      console.log("Showing status for parcel ID:", id);
      if (
        ((statutColi.value.code === "POSTPONED" ||
          statutColi.value.code === "PROGRAMED") &&
          !date_retour.value) ||
        ((statutColi.value.code === "REFUSE" ||
          statutColi.value.code === "CANCELED" ||
          statutColi.value.code === "OUT_OF_AREA") &&
          !commentStatu.value)
      ) {
        // Do nothing
      } else {
        formStatus.id = 0;
        formStatus.status = "";
        formStatus.status_id = 0;
        formStatus.comment = "";
        statutColi.value = {};
        commentStatu.value = "";
        date_retour.value = "";
        formStatus.id = id;
      }

      const modalEl = document.getElementById("modal_update_status");
      if (modalEl) {
        const modal = new Modal(modalEl);
        modal.show();
      }
    }

    const PriceColis = ref<any>({});
    // Edit parcel price
    async function editPrice(row) {
      PriceColis.value = null;
      PriceColis.value = row;
      nextTick(() => {
        const modalEl = document.getElementById("modal_update_price");
        console.log(modalEl);
        if (modalEl) {
          const modal = new Modal(modalEl);
          modal.show();
        }
      });
    }

    // -------------------------------
    const rowUpdateFees = ref(null);
    const isOpenFees = ref(false);

    // Edit parcel fees
    async function handleFeesClick(row) {
      rowUpdateFees.value = null;
      isOpenFees.value = false;
      rowUpdateFees.value = row;
      nextTick(() => {
        const modalEl = document.getElementById("modal_fees_edit");
        console.log(modalEl);
        if (modalEl) {
          isOpenFees.value = true; // triggers watcher in child

          const modal = new Modal(modalEl);
          modal.show();
        }
      });
    }

    // -------------------------------
    // Edit delivered status
    async function EditLivredStatut(row) {
      console.log("Editing delivered status for:", row);
      // TODO: Open delivered status modal
    }

    // -------------------------------
    // Mark parcel as paid
    async function PaidParcel(row) {
      console.log("Marking parcel as paid:", row);
      // TODO: Implement payment logic
    }

    async function editPriceStatut() {
      const data = new FormData();
      data.append("id_colis", String(formStatus.id));
      data.append("status_id", String(statutColi.value?.id ?? ""));
      data.append("status", String(statutColi.value?.code ?? ""));
      data.append("comment", commentStatu.value ?? "");
      data.append(
        "date_report",
        date_retour.value ? moment(date_retour.value).format("DD/MM/YYYY") : ""
      );
      data.append("user_id", String(user?.id ?? ""));

      if (
        (statutColi.value?.code === "REFUSE" ||
          statutColi.value?.code === "CANCELED" ||
          statutColi.value?.code === "OUT_OF_AREA") &&
        !commentStatu.value
      ) {
        showToast("Champs commentaire est obligatoire", "error");
      } else if (
        (statutColi.value?.code === "POSTPONED" ||
          statutColi.value?.code === "PROGRAMED") &&
        !date_retour.value
      ) {
        showToast("Champs date est obligatoire", "error");
      } else {
        api
          .post(`/api/auth/coli_edit_status/${formStatus.id}`, data)
          .then((res) => {
            if (res.data.message === "success") {
              const modalEl = document.getElementById("modal_update_status");
              const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
              modal.hide();
              getRecords();
              showToast("Statut modifié avec succès", "success");
            }
          })
          .catch(() => {
            showToast("Erreur lors de la modification du statut", "error");
          });
      }
    }

    async function EditPriceColis() {
      if (!PriceColis.value || !PriceColis.value.id) {
        showToast("Colis introuvable", "error");
        return;
      }
      const data = new FormData();
      data.append("id_colis", String(PriceColis.value.id));
      data.append("price", String(PriceColis.value.price));
      data.append("user_id", String(user?.id ?? ""));
      try {
        const res = await api.post(
          `/api/auth/coli_edit_price/${PriceColis.value.id}`,
          data
        );
        if (res.data.message === "success") {
          getRecords();
          showToast("Prix modifié avec succès", "success");
          hideModal("modal_update_price");
        } else if (res.data.message === "no edit") {
          showToast("Vous pouvez pas modifier ce colis", "error");
        } else if (res.data.message === "no access") {
          showToast(
            "Vous avez pas les droits nécessaire pour modifier ce colis",
            "error"
          );
        } else if (res.data.message === "no parcel") {
          showToast("Ce colis est introuvable", "error");
        }
      } catch (error) {
        showToast("Erreur lors de la modification du prix", "error");
      }
    }

    // Debounce timer for search input
    const debounceTimer = ref<number | undefined>(undefined);

    function handleInput() {
      if (debounceTimer.value) clearTimeout(debounceTimer.value);
      debounceTimer.value = window.setTimeout(() => {
        filter.searchTerm = searchTerm.value;
        filter.current_page = 1;
        getRecords();
      }, 500);
    }

    const todayDt = moment();
    const firstDt = moment().subtract(8, "M");
    const selectedDate = ref(null);
    const showCalendarBox = ref(false);
    const filtersOpen = ref(false);

    const activeFilterCount = computed(() => {
      let count = 0;
      if (client_filter.value?.id) count++;
      if (zone_filter.value?.id) count++;
      if (city_filter.value?.id) count++;
      if (liv_filter.value?.id) count++;
      if ((etat_filter.value as any)?.name) count++;
      if (status_filter.value?.id) count++;
      if (selectedDate.value?.[0]) count++;
      return count;
    });

    function removeFilter(key: string) {
      const empty = { id: "", name: "" };
      if (key === "client") client_filter.value = { ...empty };
      else if (key === "zone") zone_filter.value = { ...empty };
      else if (key === "city") city_filter.value = { ...empty };
      else if (key === "liv") liv_filter.value = { ...empty };
      else if (key === "etat") etat_filter.value = "";
      else if (key === "status") status_filter.value = { id: "", name: "", code: "" };
      else if (key === "date") selectedDate.value = null;
      applyFilters();
    }

    function showCalendar() {
      showCalendarBox.value = !showCalendarBox.value;
    }

    function callPhone(phone: string) {
      window.open(`tel:${phone}`);
    }

    function applyFilters() {
      filter.searchZone = zone_filter.value?.id || "";
      filter.searchCustomer = client_filter.value?.id || "";
      filter.searchLiv = liv_filter.value?.id || "";
      filter.searchCity = city_filter.value?.id || "";
      filter.searchEtat = etat_filter.value || "";
      filter.searchStatut = status_filter.value?.code || "";

      // Date range
      filter.searchSDate = selectedDate.value?.[0]
        ? selectedDate.value[0].format("YYYY-MM-DD")
        : "";

      filter.searchEDate = selectedDate.value?.[1]
        ? selectedDate.value[1].format("YYYY-MM-DD")
        : "";

      filter.searchTypeDate = type_date_filter.value || "";
      filter.searchBPL = add_bp_filter.value || "";
      filter.searchRef = ref_filter.value || "";
      filter.searchEmail = email_filter.value || "";
      filter.searchTerm = searchTerm.value || "";
      filter.searchPrice = price_filter.value || "";

      filter.current_page = 1;
      getRecords();
    }

    function resetFilters() {
      // UI values
      zone_filter.value = null;
      client_filter.value = null;
      liv_filter.value = null;
      city_filter.value = null;
      etat_filter.value = null;
      status_filter.value = null;
      type_date_filter.value = "";
      add_bp_filter.value = "";
      ref_filter.value = "";
      email_filter.value = "";
      price_filter.value = "";
      searchTerm.value = "";
      selectedDate.value = null;

      // API filter object
      Object.assign(filter, {
        searchTerm: "",
        searchCustomer: "",
        searchZone: "",
        searchLiv: "",
        searchCity: "",
        searchEtat: "",
        searchStatut: "",
        searchSDate: "",
        searchEDate: "",
        searchTypeDate: "",
        searchBPL: "",
        searchRef: "",
        searchEmail: "",
        searchPrice: "",
        current_page: 1,
      });

      getRecords();
    }

    const paginatedPages = computed(() => {
      const total = filter.last_page;
      const current = filter.current_page;
      const delta = 2;
      const pages: (number | string)[] = [];

      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }

      pages.push(1);
      const left = Math.max(2, current - delta);
      const right = Math.min(total - 1, current + delta);
      if (left > 2) pages.push("...");
      for (let i = left; i <= right; i++) pages.push(i);
      if (right < total - 1) pages.push("...");
      pages.push(total);
      return pages;
    });

    return {
      leadPlaceholder,
      auth,
      rows,
      goToPage,
      filter,
      getDate,
      oldPhone,
      globalHelper,
      removeHours,
      reported,
      resolveStatuParcel,
      resolveUserStatusVariant,
      getBadgeConfig,
      zone_filter,
      client_filter,
      liv_filter,
      city_filter,
      status_filter,
      showRefsEdit,
      showRefs,

      etat_filter,
      type_date_filter,
      add_bp_filter,
      ref_filter,
      email_filter,
      price_filter,
      searchTerm,

      clientList,
      zonesList,
      citiesList,
      livreurList,
      etatList,
      status,

      filterClients,
      onSearchDrivers,
      filterList,
      removeFilter,
      exportColis,
      reportTodayColis,
      countExportToday,

      showParcelBons,
      voirItem,
      user,
      handleInput,
      PriceColis,
      TicketsPDF,
      showDetailSuivi,
      showDetails,
      editParcel,
      SubstrPhone,
      ShareCust,
      ShareLivreur,
      Destroy_Coli,
      adminDestroy,
      showUpdate,
      showStatus,
      editPrice,
      handleFeesClick,
      EditLivredStatut,
      PaidParcel,

      // modals
      minDate,
      statutColi,
      commentStatu,
      date_retour,
      StatusList2,
      editPriceStatut,

      colisId,
      isOpen,
      coliDetails,
      getRecords,
      rowUpdate,
      rowUpdateFees,

      isLoader,
      isOpenEdit,
      hideModal,
      EditPriceColis,
      isOpenFees,
      date_filter,
      selectedDate,
      firstDt,
      todayDt,
      showCalendar,
      showCalendarBox,
      filtersOpen,
      activeFilterCount,
      changePerPage,
      callPhone,

      applyFilters,
      resetFilters,
      paginatedPages,
    };
  },
};
</script>

<style scoped>
/* ── Show button (collapsed state) ── */
.refs-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.15s;
  font-size: 14px;
}
.refs-btn:hover { opacity: 0.85; transform: scale(1.08); }

.refs-btn--show {
  background-color: #ffa117;
  color: #fff;
}
.refs-btn--hide {
  background-color: transparent;
  color: #dc3545;
  border: 1px solid #dc3545;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 12px;
  padding: 0;
  flex-shrink: 0;
}
.refs-btn--hide:hover {
  background-color: #dc3545;
  color: #fff;
}

/* ── Expanded refs card ── */
.refs-expanded {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 6px 8px;
  min-width: 140px;
  max-width: 200px;
}

.refs-expanded__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  padding-bottom: 5px;
  border-bottom: 1px solid #dee2e6;
}

.refs-expanded__title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
}

/* ── Refs list ── */
.refs-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.refs-list li a {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: #0d6efd;
  cursor: pointer;
  text-decoration: none;
  padding: 2px 5px;
  border-radius: 4px;
  transition: background 0.12s, color 0.12s;
  white-space: nowrap;
}
.refs-list li a:hover {
  background: #e7f1ff;
  color: #0a58ca;
}
.refs-list li a i {
  font-size: 11px;
  color: #6c757d;
}
</style>